<template>
  <div class="col-12 bg-white-primary mx-auto">
    <div
      class="d-flex flex-column flex-md-row justify-content-md-start align-items-center mb-1 mx-0 ps-0 ps-md-3 pe-0"
    >
      <div class="size-16 fw-bold text-center pt-4 pb-2 pb-md-4 ps-0 ps-md-2">経験職種</div>
      <span class="tag-thirdly size-10 ms-2 mb-4 mb-md-0">必須</span>
    </div>
    <div class="row mx-0 px-3 py-4 border-top">
      <div
        v-if="baseInformationStore.profile_resume_occupations.length > 0"
        class="d-flex gap-1 gap-md-3"
      >
        <div class="col-8 col-md-4 pe-0">
          <label class="form-label size-12 d-block" for="">経験職種</label>
        </div>
        <div class="col-3 col-md-2">
          <label class="form-label size-12 d-block" for="">年数</label>
        </div>
        <div class="w-29x"></div>
      </div>
      <div
        v-for="(
          candidate_resume_occupation, index
        ) in baseInformationStore.profile_resume_occupations"
        :key="index"
        class="d-flex w-100 gap-1 gap-md-3 pb-3"
      >
        <Field
          v-slot="field"
          v-model="candidate_resume_occupation.occupation_id"
          :name="`occupation_id${index}`"
        >
          <div class="col-md-4 pe-0" :class="index > 0 ? 'col-8' : 'col-9'">
            <select
              :class="`form-select select-input-primary size-14 pe-2r pe-md-1r p-0d8 overflow-hidden word-break-break-all truncate truncate-text ${
                isResumeOccupationInvalid &&
                resumeOccupationError &&
                candidate_resume_occupation.occupation_id === 0
                  ? 'is-invalid'
                  : ''
              }`"
              v-bind="field"
              @change="onChangeIndustryId($event, candidate_resume_occupation, index)"
            >
              <option :value="0" selected disabled>選択してください</option>
              <optgroup
                v-for="occupation in filteredOccupations(index)"
                :key="occupation.id"
                :label="occupation.name"
              >
                <option v-for="item in occupation.children" :key="item.id" :value="item.id">
                  {{ item.name }}
                </option>
              </optgroup>
            </select>
          </div>
        </Field>
        <Field
          v-slot="field"
          v-model="candidate_resume_occupation.years_of_experience"
          :name="`years_of_experience${index}`"
        >
          <div class="col-3 col-md-2 pe-0">
            <select
              class="form-select select-input-primary size-14 p-0d8"
              v-bind="field"
              @change="onChangeYear($event, candidate_resume_occupation, index)"
            >
              <option
                v-for="year in yearDefault"
                :key="year.value"
                :value="year.value"
                :selected="candidate_resume_occupation.years_of_experience === year.value"
              >
                {{ year.name }}
              </option>
            </select>
          </div>
        </Field>
        <div class="d-flex justify-content-center align-items-center flex-column ms-2">
          <img
            alt="Remove icon"
            class="h-29x w-29x cursor-pointer"
            src="@/assets/images/icons/remove-icon.svg"
            @click="() => onRemoveOccupation(candidate_resume_occupation.occupation_id)"
          />
        </div>
      </div>
      <div v-if="isResumeOccupationInvalid" class="d-block invalid-feedback">
        {{ resumeOccupationError }}
      </div>
      <div class="row justify-content-center w-100 m-0">
        <button
          type="button"
          class="btn btn-outline-secondary d-flex justify-content-center align-items-center size-12 w-12 py-0d75 rounded-5 h-36x"
          @click="addItemForResumeOccupation"
        >
          <div class="icon w-1"></div>
          <span class="ms-2">経験職種を追加する</span>
        </button>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onMounted, ref, watchEffect, computed } from 'vue';
import { Field, useFieldArray } from 'vee-validate';
import { OccupationPrimary } from '@/types/models/occupation';
import { getOccupations } from '@/apis/candidate';
import { useBaseInformationStore } from '@/stores/resume-candidate/BasicInformation.store';
import { yearDefault } from '@/constant';
import { useFormErrorsStore } from '@/stores/resume-candidate/Errors.store';

const occupations = ref<OccupationPrimary[]>([]);

const baseInformationStore = useBaseInformationStore();
const { update } = useFieldArray('profile_resume_occupations');
const formErrorsStore = useFormErrorsStore();
const resumeOccupationError = ref('');

onMounted(async () => {
  await loadOccupations();
});

watchEffect(() => {
  resumeOccupationError.value = formErrorsStore.errors.profile_resume_occupations || '';
});

const filteredOccupations = (index: number): OccupationPrimary[] => {
  return occupations.value.map((occupation) => {
    const clonedOccupation = { ...occupation };
    clonedOccupation.children = clonedOccupation.children?.filter((child) => {
      return !baseInformationStore.profile_resume_occupations.some(
        (resume_occupation, i) => resume_occupation.occupation_id === child.id && i !== index
      );
    });
    return clonedOccupation;
  });
};

const loadOccupations = async (): Promise<void> => {
  const response = await getOccupations();
  if (response.status === 200) occupations.value = response.data;
};

const addItemForResumeOccupation = (): void => {
  baseInformationStore.profile_resume_occupations.push({
    occupation_id: 0,
    years_of_experience: 0,
  });
};

const onChangeIndustryId = ($event: any, candidate_resume_occupation: any, index: number): void => {
  update(index, { ...candidate_resume_occupation, occupation_id: $event.target.value });
  baseInformationStore.profile_resume_occupations[index].occupation_id = +$event.target.value;
};

const onChangeYear = ($event: any, candidate_resume_occupation: any, index: number): void => {
  update(index, { ...candidate_resume_occupation, years_of_experience: $event.target.value });
  baseInformationStore.profile_resume_occupations[index].years_of_experience = +$event.target.value;
};

const onRemoveOccupation = (id: number): void => {
  baseInformationStore.profile_resume_occupations =
    baseInformationStore.profile_resume_occupations.filter(
      (occupation) => occupation.occupation_id !== id
    );
};

const isResumeOccupationInvalid = computed(() => {
  return (
    resumeOccupationError.value &&
    (baseInformationStore.profile_resume_occupations.length === 0 ||
      baseInformationStore.profile_resume_occupations[0].occupation_id === 0)
  );
});
</script>
